import {assert, assertAndContinue} from 'Asserts';
import {MixtureMeasurementUnit} from 'graphql-client/__generated/graphqlGlobalTypes';
import {IngredientDataFromParent} from 'notebook/integrations/registry/MixtureGroupConstants';
import {
  CATALOG_IDENTIFIER_COLUMN_INDEX,
  COMPONENT_BIOENTITY_COLUMN_INDEX,
  MixtureIngredientsStructuredTable,
  MixtureIngredientsStructuredTableRowMetadata,
  UNITS_COLUMN_INDEX,
} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {MixtureMetadataStructuredTable} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import addRowsToTable from 'notebook/structured-tables/util/addRowsToTable';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {getTableWithUpdatedCellByColumnType} from 'notebook/structured-tables/util/StructuredTableUpdaters';
import {Row, TableColumnType} from 'notebook/tables/TableConstants';
import {getTypedValueFromBioentitySummary} from 'notebook/tables/typed-values/DataTypes';

import {getMixtureIngredientsStructuredTableAdapter} from './MixtureTableAdapters';

export function getUpdatedTableWithUnitsFromParent(
  table: MixtureMetadataStructuredTable,
  units: MixtureMeasurementUnit
): MixtureMetadataStructuredTable {
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  return getTableWithUpdatedCellByColumnType(table, 0, TableColumnType.UNIT_SELECTOR, {
    text: '',
    typedValue: {rawValue: units, type: 'STRING'},
  });
}

export function getUpdatedTableWithIngredientRowsFromParent(
  table: MixtureIngredientsStructuredTable,
  ingredientsFromParent: Array<IngredientDataFromParent>
): MixtureIngredientsStructuredTable {
  const newRows: Array<Row> = [];
  const newRowMetadata: Array<MixtureIngredientsStructuredTableRowMetadata> = [];
  ingredientsFromParent.forEach((ingredientFromParent, i) => {
    const {
      componentBioentitySummary,
      units,
      catalogIdentifier,
      parentIngredientId,
      parentIngredientAmount,
      notes,
    } = ingredientFromParent;
    const row = getBlankRow(i, table.columns, null);
    row[COMPONENT_BIOENTITY_COLUMN_INDEX] = {
      text: '',
      typedValue: getTypedValueFromBioentitySummary(componentBioentitySummary),
    };
    row[CATALOG_IDENTIFIER_COLUMN_INDEX] = {text: catalogIdentifier || ''};
    row[UNITS_COLUMN_INDEX] = {text: units};
    const notesColumnIndex = table.columns.findIndex(
      (column) => column.columnType === TableColumnType.INGREDIENT_NOTES
    );
    if (notesColumnIndex !== -1) {
      // The user is allowed to delete this column
      row[notesColumnIndex] = {text: notes || ''};
    }
    newRows.push(row);
    newRowMetadata.push({
      parentIngredientId,
      parentIngredientAmount,
      isCatalogIdentifierInherited: catalogIdentifier != null,
      ingredientId: null,
    });
  });
  const newTable = addRowsToTable(
    newRows,
    table,
    getMixtureIngredientsStructuredTableAdapter(),
    newRowMetadata,
    {
      addAtBeginning: true,
    }
  );
  assertAndContinue(
    newTable != null,
    'Error adding inherited ingredient rows in MixtureIngredientsStructuredTableAdapter'
  );
  return newTable || table;
}

export function getUpdatedTableWithoutIngredientRowsFromParent(
  table: MixtureIngredientsStructuredTable
): MixtureIngredientsStructuredTable {
  const updatedRows = table.rows.filter((row) => row.metadata.parentIngredientId == null);
  return {
    ...table,
    rows: updatedRows.length
      ? updatedRows
      : [getMixtureIngredientsStructuredTableAdapter().getBlankRow(table.metadata, 0, table.columns)],
  };
}
